import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../SharedControlPanel.scss';
import './ConnectorPanel.scss';
import { DefaultProjectUser, ProjectUser, ProjectUsers } from '../../../../../types/ProjectUser';
import TickIcon from '../../../../../images/tick-destination-icon.png';
import { InviteProjectUserModal } from '../../../../Modals/InviteProjectUserModal/InviteProjectUserModal';
import useProject from '../../../../../hooks/useProject';
import { UserTypeEnum } from '../../../../../types/enums/UserTypesEnum';
import Help from '../../../../Help/Help';
import PageIcon from '../../../../../images/chat-panel-Icon.png';
import env from '../../../../../Env';

type ConnectorPanelProps = {
  projectUsers: ProjectUsers;
  isReadOnly: boolean;
};

const ConnectorPanel = ({ projectUsers, isReadOnly }: ConnectorPanelProps) => {
  const { t } = useTranslation();
  const { project } = useProject();
  const maxNumberOfConnectors = 4;
  const [isModalOpen, setIsModalOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedProjectUser, setSelectedProjectUser] = useState<ProjectUser>({
    ...DefaultProjectUser,
    userType: UserTypeEnum.Captioner,
  });

  const openInviteProjectUserModal = (name = '', emailAddress = '', id = '', accessToken = '') => {
    setSelectedProjectUser({
      ...DefaultProjectUser,
      id: id,
      name: name,
      emailAddress: emailAddress,
      projectId: project?.id ?? '',
      userType: UserTypeEnum.Connector,
      accessToken: accessToken,
    });
    setIsModalOpen(true);
  };

  const getConnectorFromProjectUsers = (): ProjectUsers => {
    if (!projectUsers || projectUsers.length === 0) {
      return [];
    }

    const users = projectUsers.filter(function (obj) {
      return obj.userType === UserTypeEnum.Connector;
    });

    return users || [];
  };

  const createAddConnectorButtons = () => {
    if (isReadOnly) {
      return null;
    }
    const addConnectorButtons = [];
    const numberOfButtonsToCreate = maxNumberOfConnectors - getConnectorFromProjectUsers().length;
    for (let index = 0; index < numberOfButtonsToCreate; index++) {
      addConnectorButtons.push(
        <button
          key={index}
          className="control-panel-section-box control-panel-section-box-new"
          onClick={() => openInviteProjectUserModal()}
          disabled={isReadOnly}
        >
          <span className="control-panel-section-box-text">
            + {t('producer.control-panel.connector.invite-connector-button')}
          </span>
        </button>,
      );
    }
    return addConnectorButtons;
  };

  return (
    <div className="captioning-panel">
      <div className="captioning-section">
        {getConnectorFromProjectUsers().map((projectUser: ProjectUser) => (
          <button
            key={projectUser?.id}
            className="control-panel-section-box"
            onClick={() =>
              openInviteProjectUserModal(
                projectUser?.name,
                projectUser?.emailAddress,
                projectUser?.id,
                projectUser?.accessToken,
              )
            }
            disabled={isReadOnly}
          >
            <span className="control-panel-section-box-text">{projectUser?.name}</span>
            <img src={TickIcon} alt="tick" className="control-panel-section-box-tick" />
          </button>
        ))}
        {createAddConnectorButtons()}
        <div className="captioning-panel-section-footer">
          <p>
            <span className="captioning-panel-section-footer-title">
              {t('producer.control-panel.connector.note')}{' '}
            </span>
            <span className="captioning-panel-section-footer-text">
              {t('producer.control-panel.connector.note-text')}
              <Help
                title="Invite Connector User"
                text="Connector users need to be invited to the event via AccessLOOP in order to connect their service or platform. They should not attempt to create or join the event independently in their own system, or set up any alternative connection. All configuration is handled automatically in the AccessLOOP backend.<br/><br/>They will only be able to connect once they join the event using the invitation link sent to them by email. Alternatively, after adding them, you can use the <b>Copy Invite Link</b> button and share this link with them manually."
              />
            </span>
          </p>
        </div>
        <hr className="control-panel-section-divider" />

        <div className="captioning-settings-section level">
          <div className="captioning-settings-title">
            <p className="captioning-settings-title-text">
              {t('producer.control-panel.connector.app-download-title')}
            </p>
            <p className="captioning-settings-section-content-value">
              <a className="link" href={`${env.authRedirect}/connector/AccessLOOP-Connector-Setup.exe`}>
                {t('producer.control-panel.connector.app-download-link')}
              </a>
              <img
                src={PageIcon}
                alt="Instructions"
                className="captioning-settings-section-download-image"
              />
            </p>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <InviteProjectUserModal
          isOpen={isModalOpen}
          contentLabel="Invite Connector Modal"
          selectedProjectUser={selectedProjectUser}
          userType={UserTypeEnum.Connector}
          handleCloseModal={() => {
            setIsModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(ConnectorPanel);
